import { LOCAL_URL, TEST_URL, isLocal } from "./misc";

const Host = isLocal ? LOCAL_URL : TEST_URL;

const api = {
  authentication: {
    register: (verifyEmail) =>
      `${Host}/user/signup?verifyByEmail=${verifyEmail}`,
    login: `${Host}/user/login`,
    generateOTP: `${Host}/user/forgot-password`,
    verifyUser: `${Host}/user/verify`,
    resendOTP: `${Host}/user/resend-otp`,
    resetPassword: `${Host}/user/reset-password`,
    // Short-lived SSO assertion for the P2P app. Minted per click (the token
    // lives ~2 minutes), never cached.
    p2pSsoToken: `${Host}/user/p2p-sso-token`,
  },

  website: {
    stats: `${Host}/website/websiteStats`,
  },

  switchRole: {
    updateRole: (id) => `${Host}/user/updateUserRole/${id}`,
  },

  userDetails: {
    // userStatus: (id) => `${Host}/user/status/${id}`,
    userPersonalDetails: (id) => `${Host}/user/${id}`,
    updateUser: (id) => `${Host}/user/updateUser/${id}`,
    juryStake: `${Host}/user/jury-stake`,
    // Admin-only `unlockStake`: the backend pays the caller's own free stake.
    unlockJuryStake: `${Host}/user/jury-stake/unlock`,
    userBankDetails: `${Host}/Bank/createBank`,
    addCollateral: `${Host}/transaction/addCollateral`,
    removeCollateral: `${Host}/transaction/removeCollateral`,
    getUserBalance: `${Host}/batch/getUserBalance`,
  },

  panel: {
    getTransactionsJuryOrdersLive: `${Host}/transaction/getTransitionJuryOrdersLive`,
    getSettledDispute: `${Host}/dispute/getAllSettledDispute`,
    getSettledAppeal: `${Host}/dispute/getSettledAppeal`,
    successTransaction: `${Host}/transaction/successTransaction`,
    cancelOrder: `${Host}/transaction/cancelOrder`,
    getTransactions: (filter, pageSize, pageNumber, custodianLiveOrders) =>
      `${Host}/transaction/getAll?filter=${filter}&pageSize=${pageSize}&pageNumber=${pageNumber}&custodianLiveOrders=${custodianLiveOrders}`,
    // userBatchEarning: `${Host}/batch/getBatchEarning`,
    userBatchEarning: (filter, pageSize, pageNumber) =>
      `${Host}/batch/getBatchEarning?filter=${filter}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
    tribunalAppealedTransactions: (pageSize, pageNumber) =>
      `${Host}/transaction/getApplealedTransactions?pageSize=${pageSize}&pageNumber=${pageNumber}`,
    tribunalCompleteTransactions: (filter, pageSize, pageNumber) =>
      `${Host}/transaction/getApplealedCompleteTransactions?filter=${filter}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
    custodianPortfolio: `${Host}/batch/getPortfolioBreakdown`,
    custodianDashboardData: `${Host}/rewardHistory/getCustodianDashboardByUserId`,
    userPortfolio: `${Host}/transaction/getUserPortfolio`,
    closeDisputeAfterJuryVotes: (transactionId) =>
      `${Host}/transaction/cancelAndResolveDispute?transactionId=${transactionId}`,
    addProofForDisputeOrAppeal: `${Host}/transaction/addDisputeOrAppealProof`,
    // Pre-flight for jury voting. MUST return allowed:true before vote()
    // is sent on-chain - the on-chain vote ties the juror's stake and
    // cannot be retried once the contract has recorded it.
    voteEligibility: (transactionId) =>
      `${Host}/transaction/voteEligibility/${transactionId}`,

    // Pre-flight for the dispute/appeal FEE. MUST be consulted before a screen
    // offers to charge: it is the only thing that knows a fee was already paid
    // from another device, another browser, or before a reload. `degraded:true`
    // means the chain could not be read and NO fee may be offered.
    stageState: (transactionId) =>
      `${Host}/transaction/stageState/${transactionId}`,
    // Durable record that a fee was paid on-chain, for when the socket is down —
    // which is exactly the case in which the old flow lost the payment.
    stageOpenedOnChain: `${Host}/transaction/stageOpenedOnChain`,

    sendTxId: (transactionId, txId) =>
      `${Host}/transaction/setTxId?transactionId=${transactionId}&transactionHash=${txId}`,
    getCustodianPoolAmount: `${Host}/crpool/getCrPool`,
    getCustodianLimits: `${Host}/transaction/getCustodianLimit`,
    getCustodianLimitsFromDb: `${Host}/transaction/getCustodianLimitFromDb`,

    getRampValue: `${Host}/batch/rampClaimable`,

    claimAvailableReward: (currency, amount) =>
      `${Host}/crpool/claimAvailableBatchRewards?currency=${currency}&amount=${amount}`,

    claimPoolReward: (currency, amount) =>
      `${Host}/crpool/claimCrPool?currency=${currency}&amount=${amount}`,

    stakingActions: `${Host}/staking/addStaking`,
    getAllStaking: (pageSize, pageNumber) => `${Host}/staking/getAllStaking?pageSize=${pageSize}&pageNumber=${pageNumber}`,

    // getTransactions: (filter, pageSize, pageNumber, custodianLiveOrders) =>
    // `${Host}/transaction/getAll?filter=${filter}&pageSize=${pageSize}&pageNumber=${pageNumber}&custodianLiveOrders=${custodianLiveOrders}`,

    getRewards: `${Host}/transaction/getUserRewardTransaction`,
    allOrders: (region) => `${Host}/transaction/getAll?region=${region}`,
    orders: (showDepositOrders, region) =>
      `${Host}/transaction/getAll?showDepositOnly=${showDepositOrders}&region=${region}`,
    ordersHistory: (userId, time, isCustodian) =>
      `${Host}/transaction/getTransitionHistoryByCustodianId?userId=${userId}&time=${time}&isCustodian=${isCustodian}`,

    orderStatus: `${Host}/transaction/changeStatus`,
    approveTransaction: `${Host}/transaction/approveTransaction`,

    getTransactionByTransactionId: (transactionId) =>
      `${Host}/transaction/getTransitionByTransactionId?transactionId=${transactionId}`,
    changePassword: `${Host}/user/change-password`,

    banks: (userId) => `${Host}/Bank/getBanksByUserId?userId=${userId}`,
    selectBank: (userId, regionId, bankId) =>
      `${Host}/Bank/createRegion?userId=${userId}&regionId=${regionId}&bankId=${bankId}`,
    deleteBank: (bankId) => `${Host}/Bank/deleteBank?bankId=${bankId}`,

    settings: {
      updatePersonalInfo: (userId) => `${Host}/user/${userId}`,
      createRegion: (userId, regionName) =>
        `${Host}/Bank/createRegion?userId=${userId}&regionName=${regionName}`,
      addRegionBank: (regionId) =>
        `${Host}/Bank/createBank?regionId=${regionId}`,
      changeLoginEmailOrNumber: `${Host}/user/updateUserEmailByUserId/phone`,
      makeDefaultBank: (userId, bankId) =>
        `${Host}/Bank/changeBankRegionStatus?userId=${userId}&bankId=${bankId}`,
    },

    createOrder: `${Host}/transaction/createTransaction`,
    uploadVideo: `${Host}/transaction/uploadVideUrl`,
    opns: {
      depositOpns: `${Host}/user/deposit/collatral`,
    },

    chat: {
      getChat: (userId, custodianId, transactionId) =>
        `${Host}/chat/getChatsByUserId?userId=${userId}&custodianId=${custodianId}&transactionId=${transactionId}`,
      createChatRoom: `${Host}/chat/createChatRoom`,
      sendMessage: `${Host}/chat/sendMessage`,
      updateChatReadStatus: `${Host}/chat/updateChatReadStatus`,
      // `userId` identifies the viewer so the server can register them as a chat
      // participant (jury/tribunal). This used to send `alacrityName` — a field
      // populated on no user — while the server read `walletAddress`, so the viewer
      // was always undefined and nobody was ever registered.
      getChatFromTxId: (txId, userId) => `${Host}/chat/getChatFromTxId?txId=${txId}&userId=${userId}`,
    },

    wallet: (userId) => `${Host}/wallet/getWalletsByUserId?userId=${userId}`,

    disputeOrders: (showDepositOnly, region) =>
      `${Host}/transaction/getAllDisputes?showDepositOnly=${showDepositOnly}&region=${region}`,
    voteOrder: `${Host}/transaction/giveVoteToDisputeOrder`,
    juryHistory: (time, filter, pageSize, pageNumber) =>
      `${Host}/transaction/getTransitionHistoryOfJury?time=${time}&filter=${filter}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
    userBatchPortfolioData: `${Host}/batch/getPortfolioBreakdown`,
    userTotalTransferDta: `${Host}/batch/getUserTotalTransfers`,
    custTotalBatches: `${Host}/batch/getCustodianTotalBatches`,
    userCurrencies: `${Host}/batch/getUserCurrencies`,
    updateProfilePicture: `${Host}/user/profilePicture`,
    editAvailability: `${Host}/user/availableStatus`,
    juryProof: {
      getRequestedProof: (transactionId) =>
        `${Host}/jury_request/getRequestsByTransactionId?transactionId=${transactionId}`,
      requestProof: `${Host}/jury_request/createRequestOfTransaction`,
      submitProof: `${Host}/jury_request/sendMessageToRequest`,
    },

    openDispute: `${Host}/transaction/openDispute`,
    openAppeal: `${Host}/transaction/openAppeal`,
    voteAppealOrder: `${Host}/transaction/giveVoteToAppealOrder`,
    getEarning: (role) => `${Host}/earnings/getEarning?role=${role}`,
    getTribunalEarning: () => `${Host}/earnings/getTribunalEarning`,
    getPendingEarnings: (role) =>
      `${Host}/earnings/getPendingEarning?role=${role}`,
    getCustEarning: `${Host}/earnings/getUserEarning`,
    claimEarning: `${Host}/earnings/claimEarning`,
    claimTribEarning: `${Host}/earnings/claimTribunalEarning`,
    claimJuryRewardPool: (amount, currency) =>
      `${Host}/transaction/claimJuryReward?amount=${amount}&currency=${currency}`,
    claimCustodianRewardPool: (amount, currency) =>
      `${Host}/transaction/claimCustodianReward?amount=${amount}&currency=${currency}`,
    claimRampAmount: (amount, currency) =>
      `${Host}/earnings/claimRampAmount?amount=${amount}&currency=${currency}`,
    referral: {
      me: `${Host}/referral/me`,
      summary: `${Host}/referral/summary`,
      history: (pageSize, pageNumber) =>
        `${Host}/referral/history?pageSize=${pageSize}&pageNumber=${pageNumber}`,
      validate: (code) =>
        `${Host}/referral/validate?code=${encodeURIComponent(code)}`,
    },
  },

  ticker: {
    getAllTickers: `${Host}/ticker/getAllTickers`,
    getExchangePrices: `${Host}/ticker/getExchangePrices`,
  },
  currencies: {
    getAllCurrencies: `${Host}/ticker/getAllTokens`,
  },
  countries: {
    getAllCountries: () => `${Host}/country`,
    getStatesOfCountry: (countryCode) => `${Host}/country/state?country-code=${countryCode}`,
    getCitiesOfCountry: (countryCode, isoCode) => `${Host}/country/city?country-code=${countryCode}&iso-code=${isoCode}`,
  }
};

export default api;